// Ads › Campaigns — ตารางแคมเปญ + ตัวเลขจากปลายทาง
//
// แต่ละช่องกดเข้าไปดูรายละเอียดของระบบนั้นได้ (KOL → รายงาน KOL · Biddable → Creative Report)
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import type { Brand, Campaign } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import {
  documentFileUrl,
  documentKeys,
  fetchDocuments,
  type CampaignDocument,
} from "@features/documents/api/documents-api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { brandCfgLink, menuPath } from "@features/navigation/model/side-menus";
import {
  biddableMetrics,
  fetchBiddableScope,
  fetchKolSummary,
  overviewKeys,
} from "@features/overview/api/overview-api";
import {
  fetchBiddableProjects,
  settingsKeys,
  type BiddableProject,
} from "@features/settings/api/settings-api";
import { fmtBaht, fmtCompact, fmtInt } from "@shared/lib/format";
import { kolRefsOf } from "@features/overview/lib/kol-refs";

/** ลิงก์ Online Ads ที่ใช้อยู่ตอนนี้ — ผูกเป็น project ก้อนเดียวต่องาน */
const projectLinksOf = (c: Campaign) => (c.links ?? []).filter((l) => l.kind === "ads_project");
/** ลิงก์รุ่นก่อน ที่ผูกทีละ Ad Account — ยังมีแถวเก่าในฐานข้อมูล */
const legacyLinksOf = (c: Campaign) => (c.links ?? []).filter((l) => l.kind === "ads");

export function CampaignsTablePage({ brand }: { brand: Brand }) {
  const cfgLink = brandCfgLink(brand);
  const { data: campaigns, isPending } = useCampaigns(brand.id);
  const list = campaigns ?? [];
  const kolKeys = kolRefsOf(campaigns);
  const hasToken = !!wazzupToken();

  // ตารางนี้ดูยอดรวมทั้งช่วงชีวิตของแคมเปญ จึงไม่ส่งช่วงวันไปทั้งสองฝั่ง
  const { data: kol } = useQuery({
    queryKey: overviewKeys.kolSummary(kolKeys, 0),
    queryFn: () => fetchKolSummary(kolKeys, 0),
    enabled: hasToken && kolKeys.length > 0,
  });
  const { data: accounts } = useQuery({
    queryKey: overviewKeys.ads(""),
    queryFn: () => fetchBiddableScope(""),
    enabled: hasToken && list.some((c) => legacyLinksOf(c).length > 0),
  });
  /**
   * ยอดของงานที่ผูกแบบ project
   *
   * ช่อง Biddable เคยกรองเฉพาะ kind "ads" ซึ่งเป็นรูปแบบเก่าที่ผูกทีละ Ad Account
   * งานที่ผูกด้วยหน้าตั้งค่าปัจจุบันเป็น "ads_project" จึงไม่เข้าเงื่อนไขเลย และขึ้น
   * "ไม่มี" ทั้งที่ในหน้าตั้งค่าแสดงว่าผูกไว้แล้ว
   */
  const { data: projects } = useQuery({
    queryKey: settingsKeys.biddableProjects,
    queryFn: fetchBiddableProjects,
    enabled: hasToken && list.some((c) => projectLinksOf(c).length > 0),
    retry: false,
  });

  /**
   * ไฟล์ Master Plan ของแบรนด์นี้ — อัปโหลดที่เมนู Finance แล้วเลือกว่าเป็นของแคมเปญไหน
   *
   * ขอทั้งก้อนของแบรนด์ครั้งเดียวแล้วแจกลงแถว ไม่ใช่ยิงต่อแคมเปญ · ใช้ query key
   * เดียวกับหน้า Finance จึงใช้แคชร่วมกันเมื่อสลับไปมาระหว่างสองหน้า
   */
  const { data: financeDocs } = useQuery({
    queryKey: documentKeys.byProduct(brand.id, "finance"),
    queryFn: () => fetchDocuments(brand.id, "finance"),
    retry: false,
  });

  const plansByCampaign = new Map<string, CampaignDocument[]>();
  for (const d of financeDocs ?? []) {
    if (d.category !== "master_plan" || !d.campaignId) continue;
    plansByCampaign.set(d.campaignId, [...(plansByCampaign.get(d.campaignId) ?? []), d]);
  }

  const detailLink = (menuSub: string, campaignId: string, ref: string) =>
    `${menuPath(brand.id, "ads", menuSub)}?campaign=${encodeURIComponent(campaignId)}&ref=${encodeURIComponent(ref)}`;

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">{brand.name} / Ads / Campaigns</div>
          <h1>Campaigns</h1>
        </div>
        <a className="btn-outline inline-btn" href={cfgLink}>
          ตั้งค่าแคมเปญ
        </a>
      </div>

      <div className="panel">
        {isPending ? (
          <p className="muted">กำลังโหลดแคมเปญ…</p>
        ) : !list.length ? (
          <div className="empty-state">
            <h3>ยังไม่มีแคมเปญ</h3>
            <p className="muted">
              สร้างแคมเปญได้ที่ <a href={cfgLink}>หน้าตั้งค่า → Campaigns</a>
            </p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="camp-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>
                      KOL <span className="th-note">total view / จำนวน KOL</span>
                    </th>
                    <th>
                      Biddable <span className="th-note">spending / reach</span>
                    </th>
                    <th>
                      MasterPlan <span className="th-note">ไฟล์จากเมนู Finance</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id}>
                      <td className="camp-cell">
                        <span className="camp-name">{c.name}</span>
                        <span className="muted small-note">
                          {(c.links ?? []).length} รายการที่ผูกไว้
                        </span>
                      </td>
                      <td>
                        <KolCell c={c} kol={kol} href={(ref) => detailLink("kol", c.id, ref)} />
                      </td>
                      <td>
                        <AdsCell
                          c={c}
                          accounts={accounts}
                          projects={projects}
                          href={(ref) => detailLink("online-ads", c.id, ref)}
                        />
                      </td>
                      <td>
                        <PlanCell c={c} files={plansByCampaign.get(c.id) ?? []} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!hasToken && (
              <p className="muted small-note">
                ยังไม่ได้เข้าสู่ระบบ Wazzup — ตัวเลขจากปลายทางจึงยังไม่แสดง (
                <Link to="/setting/staff">เข้าสู่ระบบ</Link>)
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * ไฟล์ Master Plan ของแคมเปญนี้ — กดแล้วเปิดไฟล์จริง
 *
 * ไม่มีไฟล์ไม่ได้แปลว่าขาดเสมอไป: งานที่ติ๊กว่าเป็น Monthly Content ไม่ต้องมีแผน
 * จึงบอกคนละอย่างกัน ไม่ใช่ "ไม่มี" เหมือนกันหมดจนแยกไม่ออกว่าต้องตามใคร
 */
function PlanCell({ c, files }: { c: Campaign; files: CampaignDocument[] }) {
  if (!files.length) {
    if (c.monthlyContent) {
      return <span className="cell-none">ไม่ต้องมี · Monthly</span>;
    }
    return <span className="cell-none">ไม่มี</span>;
  }
  return (
    <span className="plan-files">
      {files.map((f) => (
        // เปิดแท็บใหม่ ไม่ใช่ดาวน์โหลด — ส่วนใหญ่แค่อยากเปิดดูแผน ไม่ได้เอาไฟล์ไปใช้ต่อ
        <a
          key={f.id}
          className="cell-link plan-file"
          href={documentFileUrl(f.id)}
          target="_blank"
          rel="noreferrer noopener"
          title={`เปิด ${f.name}`}
        >
          <span className="plan-file-name">{f.name}</span>
        </a>
      ))}
    </span>
  );
}

type KolSummaryMap = Awaited<ReturnType<typeof fetchKolSummary>>;

function KolCell({
  c,
  kol,
  href,
}: {
  c: Campaign;
  kol: KolSummaryMap | undefined;
  href: (ref: string) => string;
}) {
  const links = (c.links ?? []).filter((l) => l.kind === "kol");
  if (!links.length) return <span className="cell-none">ไม่มี</span>;
  if (!kol)
    return (
      <Link className="cell-link" to={href(links[0].refId)}>
        {links.length} รายงาน <span className="muted">· ดูรายละเอียด</span>
      </Link>
    );

  const rows = links.map((l) => kol.get(l.refId)).filter((r) => !!r);
  const views = rows.reduce((a, r) => a + (r.total_views ?? 0), 0);
  const kols = rows.reduce((a, r) => a + (r.kol_count ?? 0), 0);
  return (
    <Link className="cell-link metric-cell" to={href(links[0].refId)}>
      <span className="m-main">
        {fmtCompact(views)} <span className="m-unit">views</span>
      </span>
      <span className="m-sub">
        {fmtInt(kols)} KOL · {links.length} รายงาน
      </span>
    </Link>
  );
}

function AdsCell({
  c,
  accounts,
  projects,
  href,
}: {
  c: Campaign;
  accounts: Awaited<ReturnType<typeof fetchBiddableScope>> | undefined;
  projects: BiddableProject[] | undefined;
  href: (ref: string) => string;
}) {
  const projectLinks = projectLinksOf(c);
  if (projectLinks.length) {
    return <ProjectCell links={projectLinks} projects={projects} href={href} />;
  }

  const links = legacyLinksOf(c);
  if (!links.length) return <span className="cell-none">ไม่มี</span>;
  if (!accounts)
    return (
      <Link className="cell-link" to={href(links[0].refId)}>
        {links.length} บัญชี <span className="muted">· ดูรายละเอียด</span>
      </Link>
    );

  const found = links.map((l) => biddableMetrics(accounts, l.meta)).filter((m) => !!m);
  const spend = found.reduce((a, m) => a + (m.spend ?? 0), 0);
  const reach = found.reduce((a, m) => a + (m.reach ?? 0), 0);
  return (
    <Link className="cell-link metric-cell" to={href(links[0].refId)}>
      <span className="m-main">{fmtBaht(spend)}</span>
      <span className="m-sub">
        reach {fmtCompact(reach)} · {links.length} บัญชี
      </span>
    </Link>
  );
}

/**
 * งานที่ผูกแบบ project — ยอดรวมทั้งงาน ไม่ใช่รายบัญชี
 *
 * ฝั่ง Biddable ให้ยอดใช้จ่ายรวมของ project มา แต่ไม่ได้ให้ reach จึงแสดงเท่าที่มีจริง
 * ไม่เติมช่องว่างด้วยเลขที่ไม่ได้มาจากไหน
 */
function ProjectCell({
  links,
  projects,
  href,
}: {
  links: Campaign["links"];
  projects: BiddableProject[] | undefined;
  href: (ref: string) => string;
}) {
  const codes = links.map((l) => l.refId);
  const to = href(codes[0]);
  if (!projects) {
    return (
      <Link className="cell-link" to={to}>
        {links.length} project <span className="muted">· ดูรายละเอียด</span>
      </Link>
    );
  }

  const found = codes
    .map((code) => projects.find((p) => p.code.toUpperCase() === code.toUpperCase()))
    .filter((p): p is BiddableProject => !!p);

  // ผูกไว้แต่ฝั่งเขายังไม่มีงานรหัสนี้ — บอกตรง ๆ ดีกว่าโชว์ ฿0 ซึ่งอ่านว่า "ยังไม่ใช้เงิน"
  if (!found.length) {
    return (
      // metric-cell เพื่อให้สองบรรทัดวางซ้อนกันเหมือนช่องที่มีตัวเลข ไม่ใช่ต่อกันบรรทัดเดียว
      <Link className="cell-link metric-cell" to={to}>
        <span className="m-main">—</span>
        <span className="m-sub">ยังไม่มีข้อมูลฝั่ง Biddable</span>
      </Link>
    );
  }

  const spend = found.reduce((a, p) => a + (p.spend ?? 0), 0);
  const campaigns = found.reduce((a, p) => a + (p.campaigns ?? 0), 0);
  return (
    <Link className="cell-link metric-cell" to={to}>
      <span className="m-main">{fmtBaht(spend)}</span>
      <span className="m-sub">
        {fmtInt(campaigns)} แคมเปญ · {links.length} project
      </span>
    </Link>
  );
}
